import React, { useState, useEffect, useContext } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { WebSocketContext } from '../WebSocketContext';
import config from '../config';
import { 
  Search, 
  Block, 
  Refresh, 
  FilterList, 
  Clear,
  CheckCircle
} from '@mui/icons-material';
import './../BlockedScripts.css';

const BlockedScripts = () => {
  const { isDarkMode } = useTheme();
  const { userDetails } = useContext(WebSocketContext);
  const [blockedScripts, setBlockedScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterExchange, setFilterExchange] = useState('ALL');
  const [selectedScripts, setSelectedScripts] = useState([]);

  const exchanges = [
    { value: 'ALL', label: 'All Exchanges' },
    { value: 'FUTCOM', label: 'MCX FUT' },
    { value: 'FUTSTK', label: 'NSE FUT' },
    { value: 'OPTSTK', label: 'NSE OPT' }
  ];

  const getBlockedSymbols = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}getBlockedSymbols`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer 7c85cfc0ad2328331d4784ffedb97565154cd2016ed0b2b6843a53e7f2a2343d`
        },
        body: JSON.stringify({}),
      });
      
      const data = await response.json();
      
      console.log('Blocked scripts API response:', data);
      
      if (data && Array.isArray(data)) {
        setBlockedScripts(data);
      } else if (data && typeof data === 'object') {
        // If data is an object, convert it to array
        const scriptsArray = Object.values(data);
        setBlockedScripts(scriptsArray);
      } else {
        setBlockedScripts([]);
      }
    } catch (error) {
      console.error('Error fetching blocked scripts:', error);
      setBlockedScripts([]);
    } finally {
      setLoading(false);
    }
  };


  const handleRefresh = () => {
    getBlockedSymbols();
  };

  useEffect(() => {
    getBlockedSymbols();
  }, []);

  // Filter scripts based on search query and exchange
  const filteredScripts = blockedScripts.filter(script => {
    if (!script) return false;
    
    const matchesSearch = searchQuery === '' || 
      (script.dispname && script.dispname.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (script.symbol && script.symbol.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (script.instrumentIdentifier && script.instrumentIdentifier.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesExchange = filterExchange === 'ALL' || 
      (script.segment && script.segment === filterExchange);
    
    return matchesSearch && matchesExchange;
  });

  const getExchangeLabel = (segment) => {
    const exchangeObj = exchanges.find(ex => ex.value === segment);
    return exchangeObj ? exchangeObj.label : segment;
  };

  const getExchangeColor = (segment) => {
    switch (segment) {
      case 'FUTCOM': return '#ff6b35'; // Orange for MCX
      case 'FUTSTK': return '#4caf50'; // Green for NSE FUT
      case 'OPTSTK': return '#2196f3'; // Blue for NSE OPT
      default: return '#666666';
    }
  };

  return (
    <div className={`blocked-scripts-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Header Section */}
      {/* <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <Block className="header-icon" />
            <h1>Blocked Scripts</h1>
          </div>
          <button className="refresh-btn" onClick={handleRefresh} disabled={loading}>
            <Refresh className={loading ? 'rotating' : ''} />
            <span>Refresh</span>
          </button>
        </div>
        
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-number">{blockedScripts.length}</div>
            <div className="stat-label">Total Blocked</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{filteredScripts.length}</div>
            <div className="stat-label">Filtered Results</div>
          </div>
        </div>
      </div> */}

      {/* Controls Section */}
      <div className="controls-section">
        <div className="search-container-blocked-scripts">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search blocked scripts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-blocked-scripts"
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <Clear />
            </button>
          )}
        </div>

        <div className="filter-container">
          <FilterList className="filter-icon" />
          <select
            value={filterExchange}
            onChange={(e) => setFilterExchange(e.target.value)}
            className="filter-select"
          >
            {exchanges.map(exchange => (
              <option key={exchange.value} value={exchange.value}>
                {exchange.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content Section */}
      <div className="content-section">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading blocked scripts...</p>
          </div>
        ) : filteredScripts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <CheckCircle />
            </div>
            <h3>No Blocked Scripts Found</h3>
            <p>
              {blockedScripts.length === 0 
                ? "You don't have any blocked scripts at the moment."
                : "No scripts match your current search criteria."
              }
            </p>
            {searchQuery || filterExchange !== 'ALL' ? (
              <button 
                className="clear-filters-btn"
                onClick={() => {
                  setSearchQuery('');
                  setFilterExchange('ALL');
                }}
              >
                Clear Filters
              </button>
            ) : null}
          </div>
        ) : (
          <div className="scripts-grid">
            {filteredScripts.map((script, index) => (
              <div key={`${script.instrumentIdentifier || script.symbol || 'script'}-${script.segment || 'unknown'}-${index}`} className="script-card">
                <div className="script-header">
                  <div className="script-info">
                    <h3 className="script-name">{script.dispname}</h3>
                    <p className="script-symbol">{script.symbol}</p>
                  </div>
                  <div 
                    className="exchange-badge"
                    style={{ backgroundColor: getExchangeColor(script.segment) }}
                  >
                    {getExchangeLabel(script.segment)}
                  </div>
                </div>
                
                {/* <div className="script-details">
                  <div className="detail-row">
                    <span className="detail-label">Exchange:</span>
                    <span className="detail-value">{script.segment || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Symbol:</span>
                    <span className="detail-value">{script.symbol || script.instrumentIdentifier || 'N/A'}</span>
                  </div>
                  {script.instrumentType && (
                    <div className="detail-row">
                      <span className="detail-label">Type:</span>
                      <span className="detail-value">{script.instrumentType}</span>
                    </div>
                  )}
                  {script.instrumentIdentifier && script.instrumentIdentifier !== script.symbol && (
                    <div className="detail-row">
                      <span className="detail-label">Instrument ID:</span>
                      <span className="detail-value">{script.instrumentIdentifier}</span>
                    </div>
                  )}
                </div> */}

              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default BlockedScripts;